import { useState, useEffect } from 'react';
import { useAddressSuggestions, type AddressSuggestion } from '@hooks/useAddressSuggestions';
import { Locations } from '@gql/base-types';
import { Autocomplete, TextField, InputAdornment, CircularProgress, useTheme } from '@mui/material';
import { BiSolidHome, BiLogoGoogle } from 'react-icons/bi';

interface DriverHomeAddressFieldProps {
  value: Locations | null;
  onChange: (location: Locations | null) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  driverId?: number | null;
  customerId?: number | null;
}

/** Format a Locations object into a display string */
const formatAddress = (location: Locations | null): string => {
  if (!location) return '';
  const parts = [location.address_line_one, location.city, location.state, location.zip_code].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : (location as any).address || location.name || '';
};

const DriverHomeAddressField = ({
  value,
  onChange,
  placeholder = 'Search for home address...',
  label = 'Home Address',
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  customerId,
}: DriverHomeAddressFieldProps) => {
  const theme = useTheme();

  const { suggestions: sdkSuggestions, loading: loadingGoogle, search, select, clear } = useAddressSuggestions();

  const [userInput, setUserInput] = useState<string>(() => formatAddress(value));
  const [buildingLocation, setBuildingLocation] = useState<boolean>(false);

  // Sync input text when the parent value changes externally (e.g. "Use Rooftop Address" button)
  useEffect(() => {
    setUserInput(formatAddress(value));
  }, [value]);

  /** Handle input change */
  const handleInputChange = (inputValue: string, reason: string) => {
    if (reason === 'input') {
      setUserInput(inputValue);
      search(inputValue);
    } else if (reason === 'clear') {
      setUserInput('');
      clear();
      onChange(null);
    }
    // Ignore reason === 'reset' — we manage the display text ourselves
  };

  /** Handle selection change */
  const handleSelectionChange = async (_event: any, newValue: AddressSuggestion | null) => {
    if (!newValue) return;

    if (!customerId) {
      console.error('Cannot resolve location without customerId');
      return;
    }

    // Show the selected text immediately
    setUserInput(newValue.fullText);
    setBuildingLocation(true);

    try {
      const result = await select(newValue, customerId);

      if (result.location) {
        const loc = result.location as unknown as Locations;
        setUserInput(formatAddress(loc));
        onChange(loc);
      }
    } catch (err) {
      console.error('Failed to build location from suggestion:', err);
    } finally {
      setBuildingLocation(false);
    }
  };

  return (
    <Autocomplete
      freeSolo
      value={null}
      inputValue={userInput}
      onInputChange={(_event, newValue, reason) => handleInputChange(newValue, reason)}
      onChange={handleSelectionChange}
      options={sdkSuggestions}
      getOptionLabel={(option: AddressSuggestion | string) =>
        typeof option === 'string' ? option : option?.fullText || option?.mainText || ''
      }
      loading={loadingGoogle || buildingLocation}
      disabled={disabled}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          error={error}
          helperText={helperText}
          fullWidth
          variant="outlined"
          size="small"
          label={label}
          placeholder={placeholder}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <BiSolidHome size={24} color={theme?.palette?.text?.secondary} />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {(loadingGoogle || buildingLocation) ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={({ key, ...props }, option: AddressSuggestion) => (
        <li key={key} {...props}>
          <BiLogoGoogle size={20} style={{ marginRight: 8, color: theme?.palette?.text?.secondary }} />
          {option?.fullText || option?.mainText}
        </li>
      )}
    />
  );
};

export default DriverHomeAddressField;
